// Écran de pilotage : taux de marge et dépenses dans le temps, avec curseur de
// période. Graphes en SVG inline — pas de bibliothèque, pas de dépendance.
//
// Choix assumé : de l'INTERACTIF, pas de la vidéo. Un curseur de période et une
// vidéo s'excluent — on ne survole pas, on ne filtre pas, on ne zoome pas dans
// un rendu. L'export vidéo (Remotion) reste possible plus tard, à sa place :
// sortir un clip à montrer, pas piloter.

import {
  FENETRES,
  preparerCourbe,
  detecterRuptures,
  evolution,
  preparerRentabilite,
} from "../lib/pilotage-logic.js";
import { eur, nombreFr } from "../lib/format.js";
import { entreeMarge } from "../lib/presse-papier-entrees.js";

const SVG_NS = "http://www.w3.org/2000/svg";

const el = (tag, cls, texte) => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (texte != null) n.textContent = texte;
  return n;
};
const svgEl = (tag, attrs = {}) => {
  const n = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
};

const PETROLE = "#2D7A8B";
const CORAIL = "#FF8463";

/** Libellé court d'une période ISO (« 2026-04-01 » → « avr. 26 »). */
function libellePeriode(iso) {
  const m = /^(\d{4})-(\d{2})/.exec(String(iso));
  if (!m) return String(iso);
  const noms = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  return `${noms[Number(m[2]) - 1]} ${m[1].slice(2)}`;
}

/**
 * Courbe du taux de marge.
 * Les points peu couverts sont tracés en creux, les ruptures de source barrées
 * verticalement : le lecteur voit POURQUOI la courbe décroche, au lieu de
 * conclure que ses conditions se sont dégradées.
 */
function rendreCourbe(donnees) {
  const { points, min, max } = preparerCourbe(donnees.lignes);
  const ruptures = detecterRuptures(donnees.sources);
  const box = el("div", "graphe");

  if (points.length < 2) {
    box.appendChild(el("div", "muted", "Pas assez de périodes pour tracer une évolution."));
    return box;
  }

  const L = 720, H = 220, mg = { g: 44, d: 16, h: 16, b: 30 };
  const larg = L - mg.g - mg.d, haut = H - mg.h - mg.b;
  const x = (i) => mg.g + (i / (points.length - 1)) * larg;
  const y = (v) => mg.h + haut - ((v - min) / (max - min)) * haut;

  const svg = svgEl("svg", {
    viewBox: `0 0 ${L} ${H}`,
    class: "svg-graphe",
    role: "img",
    "aria-label": `Taux de marge sur ${points.length} périodes`,
  });

  // Repères horizontaux + axe zéro appuyé (un taux se lit par rapport à zéro).
  for (const v of [min, (min + max) / 2, max]) {
    svg.appendChild(svgEl("line", {
      x1: mg.g, x2: L - mg.d, y1: y(v), y2: y(v),
      stroke: v === 0 ? "#C9D6DA" : "#E9EEF0", "stroke-width": v === 0 ? 1.5 : 1,
    }));
    const t = svgEl("text", { x: 6, y: y(v) + 4, class: "svg-axe" });
    t.textContent = `${Math.round(v)} %`;
    svg.appendChild(t);
  }

  // Bandes verticales de rupture de périmètre.
  for (const r of ruptures) {
    const i = points.findIndex((p) => p.periode === r.periode);
    if (i <= 0) continue;
    svg.appendChild(svgEl("line", {
      x1: x(i), x2: x(i), y1: mg.h, y2: mg.h + haut,
      stroke: CORAIL, "stroke-width": 1.5, "stroke-dasharray": "4 3", opacity: 0.75,
    }));
  }

  // Ligne du taux.
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.valeur)}`).join(" ");
  svg.appendChild(svgEl("path", { d, fill: "none", stroke: PETROLE, "stroke-width": 2.5,
    "stroke-linejoin": "round", "stroke-linecap": "round" }));

  points.forEach((p, i) => {
    // Point plein = couverture suffisante ; point creux = à prendre avec des
    // pincettes. La forme, pas seulement la couleur : ça reste lisible imprimé
    // et pour qui distingue mal les teintes.
    const c = svgEl("circle", {
      cx: x(i), cy: y(p.valeur), r: 4.5,
      fill: p.fiable ? PETROLE : "#FFFFFF",
      stroke: PETROLE, "stroke-width": 2,
    });
    const titre = svgEl("title");
    titre.textContent =
      `${libellePeriode(p.periode)} — ${nombreFr(p.valeur)} % de marge · ` +
      `couverture ${nombreFr(p.couverture)} % · achats ${eur(p.achat)}` +
      (p.ecartees ? ` · ${p.ecartees} lignes écartées (prix de vente inconnu)` : "");
    c.appendChild(titre);
    svg.appendChild(c);

    if (i === 0 || i === points.length - 1 || points.length <= 8) {
      const t = svgEl("text", { x: x(i), y: H - 8, class: "svg-axe", "text-anchor": "middle" });
      t.textContent = libellePeriode(p.periode);
      svg.appendChild(t);
    }
  });

  box.appendChild(svg);

  if (ruptures.length) {
    const avert = el("div", "avert");
    const quoi = ruptures
      .map((r) => {
        const parts = [];
        if (r.disparues.length) parts.push(`${r.disparues.join(", ")} s'arrête`);
        if (r.apparues.length) parts.push(`${r.apparues.join(", ")} démarre`);
        return `${libellePeriode(r.periode)} : ${parts.join(", ")}`;
      })
      .join(" · ");
    avert.textContent =
      `Traits corail : vos sources de données changent (${quoi}). ` +
      `De part et d'autre, la courbe ne mesure pas le même périmètre — une variation ` +
      `peut venir du changement de source et non de vos conditions d'achat.`;
    box.appendChild(avert);
  }
  return box;
}

/** Barres de dépenses par période. */
function rendreDepenses(lignes) {
  const box = el("div", "graphe");
  const rows = (Array.isArray(lignes) ? lignes : []).filter((l) => l.achat_ht != null);
  if (!rows.length) {
    box.appendChild(el("div", "muted", "Aucune dépense sur la période."));
    return box;
  }
  const max = Math.max(...rows.map((r) => Math.abs(Number(r.achat_ht))), 1);
  const grille = el("div", "barres");
  for (const r of rows) {
    const v = Number(r.achat_ht);
    const col = el("div", "barre");
    const jauge = el("div", "barre-jauge");
    jauge.style.height = `${Math.max((Math.abs(v) / max) * 100, 2)}%`;
    jauge.title = `${libellePeriode(r.periode)} — ${eur(v)}`;
    col.appendChild(jauge);
    col.appendChild(el("div", "barre-lbl", libellePeriode(r.periode)));
    grille.appendChild(col);
  }
  box.appendChild(grille);
  return box;
}

/** Bandeau de synthèse : le chiffre du moment, et ce qu'on peut en dire. */
function rendreSynthese(donnees, garder) {
  const { points } = preparerCourbe(donnees.lignes);
  const box = el("div", "synthese");
  if (!points.length) {
    box.appendChild(el("div", "muted", "Pas de donnée exploitable sur la période."));
    return box;
  }
  const dernier = points[points.length - 1];

  const bloc = el("div", "synth-bloc");
  bloc.appendChild(el("div", "tuile-titre", `Taux de marge — ${libellePeriode(dernier.periode)}`));
  bloc.appendChild(el("div", "tuile-valeur", `${nombreFr(dernier.valeur)} %`));
  // La couverture accompagne TOUJOURS le taux : un taux calculé sur 73 % des
  // achats sans le dire est plus dangereux que pas de taux du tout.
  bloc.appendChild(
    el("div", "tuile-detail",
      `calculé sur ${nombreFr(dernier.couverture)} % de vos achats` +
      (dernier.ecartees ? ` · ${dernier.ecartees} lignes sans prix de vente connu` : "")),
  );
  // Le bouton vient APRÈS la couverture, pas après la valeur : sur la fiche,
  // `entreeMarge` REFUSE un taux sans couverture. L'écran rend cette solidarité
  // visible — on garde le bloc entier, pas le seul chiffre.
  if (garder) {
    const e = entreeMarge(dernier, evolution(donnees.lignes, donnees.sources),
                          libellePeriode(dernier.periode));
    if (e) bloc.appendChild(garder(e));
  }
  box.appendChild(bloc);

  const evo = evolution(donnees.lignes, donnees.sources);
  const bloc2 = el("div", "synth-bloc");
  bloc2.appendChild(el("div", "tuile-titre", "Évolution sur la période"));
  if (evo.possible) {
    bloc2.appendChild(el("div", "tuile-valeur",
      `${evo.delta > 0 ? "+" : ""}${nombreFr(evo.delta)} pts`));
    bloc2.appendChild(el("div", "tuile-detail",
      `de ${nombreFr(evo.debut)} % à ${nombreFr(evo.fin)} %`));
  } else {
    // Refuser de conclure est une information, pas un échec.
    bloc2.appendChild(el("div", "tuile-valeur", "non comparable"));
    bloc2.appendChild(el("div", "tuile-detail", evo.raison));
  }
  box.appendChild(bloc2);
  return box;
}

/**
 * Marge RÉALISÉE par laboratoire — la Vague 1 du dashboard compta (vue
 * `v_rentabilite_labo`, en prod depuis le 03/09), enfin lue par un écran.
 *
 * ⚠️ Ce n'est PAS le taux de marge des blocs au-dessus, et le texte le dit :
 * là-haut, un taux THÉORIQUE (prix public appliqué aux unités achetées) ; ici,
 * les ventes RÉELLES du LGO croisées avec le coût réellement facturé. Le LGO
 * affiche 27,30 % de marge 2025 honoraires compris ; cette vue rend ~22 % hors
 * honoraires. Poser les deux sans le dire ferait conclure à une erreur de calcul.
 *
 * Le « laboratoire » est l'émetteur des lignes d'achat : un grossiste y figure
 * comme tel (~16 %), un labo direct avec sa marge propre (Thuasne ~57 %). Le
 * contraste est l'information — c'est ce que Mehdi a demandé le 02/09
 * (« combien un labo rapporte, et a rapporté »).
 */
function rendreRentabilite(renta, surChangementPeriode) {
  const box = el("div", "graphe");
  if (!renta || renta.erreur) {
    // Le bloc dit pourquoi il est vide ; le reste de l'écran vit sa vie. Le cas
    // réel : l'action serveur pas encore en ligne (« mergé ≠ déployé », vécu le
    // 19/09 sur cette même Edge Function, cinq semaines de retard).
    box.appendChild(el("div", "muted",
      "Marge réalisée indisponible" + (renta && renta.erreur ? ` : ${renta.erreur}` : ".")));
    return box;
  }
  if (!renta.periodes || !renta.periodes.length) {
    box.appendChild(el("div", "muted", "Aucune période de ventes rapprochée d'un coût d'achat pour votre officine."));
    return box;
  }

  // Le choix de période : UNE à la fois — les extractions se chevauchent, en
  // sommer deux triple le CA (piège ① de la vue). Les cumuls pluriannuels sont
  // proposés mais nommés comme tels.
  const barre = el("div", "curseur");
  barre.appendChild(el("label", "curseur-lbl", "Période"));
  const select = document.createElement("select");
  for (const p of renta.periodes) {
    const o = document.createElement("option");
    o.value = p.periode;
    o.textContent = p.est_cumul ? `${p.periode} (cumul, ne pas additionner)` : p.periode;
    if (renta.periode && p.periode === renta.periode.periode) o.selected = true;
    select.appendChild(o);
  }
  select.style.maxWidth = "340px";   // un sélecteur, pas un curseur : il n'a pas à s'étirer sur toute la barre
  select.addEventListener("change", () => surChangementPeriode(select.value));
  barre.appendChild(select);
  box.appendChild(barre);

  const r = preparerRentabilite(renta.lignes);
  if (!r.lignes.length) {
    box.appendChild(el("div", "muted", "Aucun laboratoire avec une marge calculable sur cette période."));
    return box;
  }

  const synth = el("div", "synthese");
  const b1 = el("div", "synth-bloc");
  b1.appendChild(el("div", "tuile-titre", `Marge réalisée — ${renta.periode.periode}`));
  b1.appendChild(el("div", "tuile-valeur", eur(r.totalMarge)));
  b1.appendChild(el("div", "tuile-detail",
    `${nombreFr(r.tauxGlobal)} % du CA couvert (${eur(r.totalCa)}) · ${r.nbLabos} laboratoires` +
    (r.nbSansMarge ? ` · ${r.nbSansMarge} sans coût connu, non comptés` : "")));
  synth.appendChild(b1);
  const b2 = el("div", "synth-bloc");
  b2.appendChild(el("div", "tuile-titre", "Ce que ce chiffre mesure"));
  b2.appendChild(el("div", "tuile-detail",
    "Ventes réelles du LGO × coût moyen réellement facturé, produit par produit, puis par émetteur de la " +
    "facture d'achat. Ce n'est pas le taux théorique des blocs du haut, ni la marge du LGO (qui compte " +
    "les honoraires de dispensation). Le CA couvert n'est pas le CA total : un produit vendu sans achat " +
    "facturé sur la période n'y est pas."));
  synth.appendChild(b2);
  box.appendChild(synth);

  const table = document.createElement("table");
  const thead = document.createElement("thead");
  const tr = document.createElement("tr");
  for (const [libelle, num] of [["Laboratoire (émetteur)", false], ["CA HT couvert", true], ["Marge", true], ["Taux", true], ["Part", true], ["Produits", true]]) {
    const th = el("th", null, libelle);
    if (num) th.style.textAlign = "right";
    th.style.cursor = "default";
    tr.appendChild(th);
  }
  thead.appendChild(tr);
  table.appendChild(thead);
  const tbody = document.createElement("tbody");
  const ligne = (l) => {
    const t = document.createElement("tr");
    if (l.agrege) t.className = "muted";
    t.appendChild(el("td", null, l.laboratoire));
    t.appendChild(el("td", "num", eur(l.ca_ht_couvert)));
    t.appendChild(el("td", "num", eur(l.marge_eur)));
    t.appendChild(el("td", "num", l.taux_marge_pct == null ? "—" : `${nombreFr(l.taux_marge_pct)} %`));
    t.appendChild(el("td", "num", l.part_pct == null ? "—" : `${nombreFr(l.part_pct)} %`));
    t.appendChild(el("td", "num",
      `${nombreFr(l.nb_produits)}${l.produits_extrapoles ? ` (${l.produits_extrapoles} au coût extrapolé)` : ""}`));
    return t;
  };
  for (const l of r.lignes) tbody.appendChild(ligne(l));
  if (r.agrege) tbody.appendChild(ligne(r.agrege));
  table.appendChild(tbody);
  // Le tableau va dans une `.card` (le conteneur qui défile et le dit), pas dans
  // la boîte du graphe : c'est la même mise en page que les listes de l'app.
  const carte = el("div", "card");
  carte.appendChild(table);
  const frag = document.createDocumentFragment();
  frag.appendChild(box);
  frag.appendChild(carte);
  return frag;
}

/** Rend l'écran de pilotage complet. */
export function rendrePilotage(racine, donnees, surChangementFenetre, garder = null, surChangementPeriode = null) {
  const frag = document.createDocumentFragment();

  // Curseur de période.
  const barre = el("div", "curseur");
  barre.appendChild(el("label", "curseur-lbl", "Période"));
  const input = document.createElement("input");
  input.type = "range";
  input.min = "0";
  input.max = String(FENETRES.length - 1);
  input.step = "1";
  input.value = String(Math.max(FENETRES.indexOf(donnees.jours ?? 90), 0));
  const valeur = el("span", "curseur-val", `${donnees.jours ?? 90} jours`);
  input.addEventListener("input", () => {
    valeur.textContent = `${FENETRES[Number(input.value)]} jours`;
  });
  input.addEventListener("change", () => surChangementFenetre(FENETRES[Number(input.value)]));
  barre.appendChild(input);
  barre.appendChild(valeur);
  frag.appendChild(barre);

  frag.appendChild(rendreSynthese(donnees, garder));
  frag.appendChild(el("h2", "sect", "Taux de marge dans le temps"));
  frag.appendChild(rendreCourbe(donnees));
  frag.appendChild(el("h2", "sect", "Dépenses d'achat par période"));
  frag.appendChild(rendreDepenses(donnees.lignes));
  frag.appendChild(el("h2", "sect", "Marge réalisée par laboratoire"));
  frag.appendChild(rendreRentabilite(donnees.rentabilite, surChangementPeriode ?? (() => {})));

  racine.replaceChildren(frag);
}

/**
 * Les deux appels partent ensemble ; le second est INDÉPENDANT du premier : si
 * l'action `rentabilite_labo` échoue (pas encore en ligne, panne), l'écran garde
 * sa courbe et son curseur, et le bloc du bas dit pourquoi il est vide.
 */
export async function chargerPilotage(api, jours = 90, periodeRentabilite = null) {
  const bucket = jours <= 60 ? "semaine" : "mois";
  const [marge, rentabilite] = await Promise.all([
    api("marge_periode", { jours, bucket }),
    api("rentabilite_labo", periodeRentabilite ? { periode: periodeRentabilite } : {})
      .catch((e) => ({ erreur: e && e.message ? e.message : String(e) })),
  ]);
  return { ...marge, rentabilite };
}
